import { createSlice } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import { MeasurementUnit, SiteContextModel, SiteModel, authApi } from '../apis/authApi';
import { Permission, userHasPermission } from '@customer/permissions';
import { globalStreamingClient } from '../apis/streamingApi';
import i18n from '@customer/i18n';
import { ErrorResponse } from '@dmc/ui';

export interface UserAuthData {
  permissions: Permission[];
  isLoggedIn: boolean;
  isRedirecting: boolean;
  isMaintenance: boolean;
  actionLoggingIn?: boolean;
  displayName?: string;
  displayInitials?: string;
  siteId?: string;
  siteTimezone?: string;
  siteContexts: SiteContextModel[];
  siteChanging: boolean;
  language: string;
  dateTimeFormat: string;
  units: MeasurementUnit;
  currentSite?: SiteModel;
  appVersion?: string;
  hideWelcomeDialog: boolean;
}

const fallbackLanguage = 'en';
const fallbackDateTimeFormat = 'en-AU';
const fallbackUnits = MeasurementUnit.Metric;

const loginUrl = '/auth/login?returnUrl=/home';
const logoutUrl = '/auth/logout?returnUrl=/';
const noAccessUrl = '/no-access';

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    permissions: [],
    isLoggedIn: false,
    isRedirecting: true,
    isMaintenance: false,
    actionLoggingIn: true,
    displayName: undefined,
    displayInitials: undefined,
    siteId: undefined,
    siteTimezone: undefined,
    siteContexts: [],
    siteChanging: false,
    language: fallbackLanguage,
    dateTimeFormat: fallbackDateTimeFormat,
    units: fallbackUnits,
    currentSite: undefined,
    appVersion: undefined,
    hideWelcomeDialog: true,
  } as UserAuthData,
  reducers: {
    login: state => {
      window.location.href = loginUrl;
    },
    logout: state => {
      state.isLoggedIn = false;
      state.isRedirecting = true;
      state.isMaintenance = false;
      state.actionLoggingIn = false;
      state.permissions = [];
      state.displayName = undefined;
      state.displayInitials = undefined;
      state.siteId = undefined;
      state.siteTimezone = undefined;
      state.siteContexts = [];
      state.language = fallbackLanguage;
      state.dateTimeFormat = fallbackDateTimeFormat;
      state.units = fallbackUnits;
      state.currentSite = undefined;
      state.appVersion = undefined;
      state.hideWelcomeDialog = true;

      console.debug('navigating to logout');
      window.location.href = logoutUrl;
    },
    inMaintenance: state => {
      state.isLoggedIn = false;
      state.isRedirecting = false;
      state.isMaintenance = true;
      state.actionLoggingIn = false;
      state.permissions = [];
      state.displayName = undefined;
      state.displayInitials = undefined;
      state.siteId = undefined;
      state.siteTimezone = undefined;
      state.siteContexts = [];
      state.language = fallbackLanguage;
      state.dateTimeFormat = fallbackDateTimeFormat;
      state.units = fallbackUnits;
      state.currentSite = undefined;
      state.appVersion = undefined;
      state.hideWelcomeDialog = true;

      console.debug('maintenance mode');
    },
    setLanguage: (state, action) => {
      state.language = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addMatcher(authApi.endpoints.me.matchFulfilled, (state, { payload }) => {
      state.isLoggedIn = true;
      state.isRedirecting = false;
      state.isMaintenance = false;
      state.actionLoggingIn = false;
      state.permissions = payload.permissions;
      state.displayName = payload.displayName;
      state.displayInitials = payload.initials;
      state.siteId = payload.currentSite?.id;
      state.siteTimezone = payload.currentSite?.timezone;
      state.siteContexts = payload.siteContexts;
      state.language = i18n.language;
      state.dateTimeFormat = payload.dateTimeNumberFormat;
      state.units = payload.units;
      state.currentSite = payload.currentSite;
      state.appVersion = payload.appVersion;
      state.hideWelcomeDialog = payload.hideWelcomeDialog;

      if (userHasPermission(state, Permission.SiteDataRead)) {
        globalStreamingClient.connect();
      }
    });
    builder.addMatcher(authApi.endpoints.me.matchRejected, (state, { payload }) => {
      state.isLoggedIn = false;
      state.actionLoggingIn = true;
      state.permissions = [];
      state.displayName = undefined;
      state.displayInitials = undefined;
      state.siteId = undefined;
      state.siteTimezone = undefined;
      state.siteContexts = [];
      state.language = fallbackLanguage;
      state.dateTimeFormat = fallbackDateTimeFormat;
      state.units = fallbackUnits;
      state.currentSite = undefined;
      state.appVersion = undefined;
      state.hideWelcomeDialog = true;

      globalStreamingClient.disconnect();

      if (ErrorResponse.isStatusError(payload)) {
        if (payload.status === 503) {
          state.isMaintenance = true;
          state.isRedirecting = false;
          console.debug('maintenance mode');
        } else if (payload.status === 403) {
          console.debug('navigating to no access');
          state.isMaintenance = false;
          state.isRedirecting = false;
          window.location.href = noAccessUrl;
        } else if (payload.status === 401) {
          console.debug('navigating to login');
          state.isMaintenance = false;
          state.isRedirecting = true;
          window.location.href = loginUrl;
        }
      }
    });
    builder.addMatcher(authApi.endpoints.changeSite.matchPending, state => {
      state.siteChanging = true;
    });
    builder.addMatcher(authApi.endpoints.changeSite.matchFulfilled, state => {
      state.siteChanging = false;
    });
    builder.addMatcher(authApi.endpoints.changeSite.matchRejected, state => {
      state.siteChanging = false;
    });
  },
});

export default authSlice.reducer;

export const { login, logout, inMaintenance, setLanguage } = authSlice.actions;

export const selectCurrentUser = (state: RootState) => state.auth;
